import { useEffect, useState } from 'react';
import { Crown, ExternalLink, Loader2, Sparkles, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import useSubscription from '@/hooks/useSubscription';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

/*
 * SubscriptionStatusCard
 *
 * Profile-page card that mirrors the current billing state. Three
 * primary states:
 *
 *   1. Pro active   → "You're on GroovLens Pro", show next renewal date,
 *                     'Manage subscription' button (opens Stripe Portal).
 *   2. Trialing     → days-left meter + 'See plans' CTA.
 *   3. Locked       → lockout copy + 'Subscribe to restore' CTA.
 *
 * Loading is graceful — we show a skeleton-like placeholder until the
 * auth context resolves rather than an empty white box.
 */
export default function SubscriptionStatusCard() {
  const { sub, isPro, isTrialing, isLocked, daysLeft, graceDaysLeft } = useSubscription();
  const [openingPortal, setOpeningPortal] = useState(false);

  const handlePortal = async () => {
    setOpeningPortal(true);
    try {
      const { data } = await api.post('/billing/portal', {
        return_url: `${window.location.origin}/profile`,
      });
      if (data?.url) window.location.href = data.url;
      else toast.error('Could not open billing portal');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not open billing portal');
    } finally {
      setOpeningPortal(false);
    }
  };

  if (!sub) {
    return (
      <div className="rounded-xl border border-border bg-card p-4 animate-pulse">
        <div className="h-5 w-40 bg-secondary rounded mb-2" />
        <div className="h-3 w-64 bg-secondary/60 rounded" />
      </div>
    );
  }

  if (isPro) {
    const nextBilling = sub.current_period_end
      ? new Date(sub.current_period_end).toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })
      : null;
    const planLabel = sub.plan === 'annual' ? 'Annual ($47.90/yr)' : 'Monthly ($4.99/mo)';
    return (
      <div
        className="rounded-xl border border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/10 p-4 space-y-3"
        data-testid="subscription-pro-card"
      >
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-lg bg-[hsl(var(--primary))] grid place-items-center text-[hsl(var(--primary-foreground))]">
            <Crown className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">You&apos;re on GroovLens Pro</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {planLabel}
              {nextBilling && <> · next billing {nextBilling}</>}
            </p>
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            variant="secondary"
            size="sm"
            onClick={handlePortal}
            disabled={openingPortal}
            className="rounded-lg"
            data-testid="subscription-manage-button"
          >
            {openingPortal ? (
              <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Opening…</>
            ) : (
              <><Settings className="w-4 h-4 mr-1.5" /> Manage subscription <ExternalLink className="w-3.5 h-3.5 ml-1" /></>
            )}
          </Button>
        </div>
      </div>
    );
  }

  if (isLocked) {
    return (
      <div
        className="rounded-xl border border-[#FF7A6A]/40 bg-[#FF7A6A]/10 p-4 space-y-3"
        data-testid="subscription-locked-card"
      >
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-lg bg-[#FF7A6A] grid place-items-center text-[#1d0a08]">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">Your trial has ended</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Crates stay readable
              {graceDaysLeft != null ? <> for {graceDaysLeft} more {graceDaysLeft === 1 ? 'day' : 'days'}</> : null}.
              Subscribe to resume scanning.
            </p>
          </div>
        </div>
        <div className="flex justify-end">
          <Link to="/pricing">
            <Button size="sm" className="rounded-lg" data-testid="subscription-resubscribe-button">
              <Sparkles className="w-4 h-4 mr-1.5" /> Subscribe to Pro
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Trialing
  return (
    <div
      className="rounded-xl border border-border bg-card p-4 space-y-3"
      data-testid="subscription-trial-card"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 rounded-lg bg-[hsl(var(--primary))] grid place-items-center text-[hsl(var(--primary-foreground))]">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">Free trial</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {daysLeft != null
              ? <><strong className="text-foreground">{daysLeft}</strong> {daysLeft === 1 ? 'day' : 'days'} remaining · full access</>
              : <>Trial active · full access</>
            }
          </p>
        </div>
      </div>
      <div className="flex justify-end">
        <Link to="/pricing">
          <Button size="sm" className="rounded-lg" data-testid="subscription-see-plans-button">
            <Crown className="w-4 h-4 mr-1.5" /> See plans
          </Button>
        </Link>
      </div>
    </div>
  );
}
